import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraDataTable, SpecraProgress, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { cmDate } from '../../../pages/Delivery/Deliverables/lib/format';
import { bulkGroupLine, groupByDeliverable } from './deliveryActionBulk';
export const BULK_PAGE_SIZE = 50;
function resultCounts(rows) {
    let ok = 0;
    let failed = 0;
    let skipped = 0;
    for (const r of rows){
        if (r.result?.status === 'ok') ok++;
        else if (r.result?.status === 'fail' && r.result.message !== '') failed++;
        else if (r.result?.status === 'skipped') skipped++;
    }
    return {
        ok,
        failed,
        skipped,
        waiting: rows.length - ok - failed - skipped
    };
}
function countsText(c, started) {
    if (!started) return '—';
    const parts = [];
    if (c.ok) parts.push(`${c.ok} applied`);
    if (c.skipped) parts.push(`${c.skipped} skipped`);
    if (c.failed) parts.push(`${c.failed} failed`);
    if (c.waiting) parts.push(`${c.waiting} waiting`);
    return parts.join(' · ') || '—';
}
function refusalLine(r) {
    const where = [
        r.deliverable,
        r.dueOn ? `due ${cmDate(r.dueOn, r.dueOn)}` : ''
    ].filter(Boolean);
    return `Row ${r.row}${where.length ? ` · ${where.join(', ')}` : ''}: ${r.reason}`;
}
/**
 * A bulk Delivery card's body: what the change is, the rows grouped by
 * deliverable with their results, every row on demand (a page at a time),
 * and the rows Specra did not propose, with why.
 */ export function BulkActionSummary({ groups, rows, columns, started, progress }) {
    const [showRows, setShowRows] = useState(false);
    const [page, setPage] = useState(0);
    const [showRefused, setShowRefused] = useState(false);
    const byDeliverable = groupByDeliverable(rows);
    const pages = Math.max(1, Math.ceil(rows.length / BULK_PAGE_SIZE));
    const safePage = Math.min(page, pages - 1);
    const slice = rows.slice(safePage * BULK_PAGE_SIZE, (safePage + 1) * BULK_PAGE_SIZE);
    const refused = groups.flatMap((g)=>g.refused ?? []);
    const refusedTotal = groups.reduce((n, g)=>n + (g.refusedCount ?? 0), 0);
    const unchangedTotal = groups.reduce((n, g)=>n + (g.unchangedCount ?? 0), 0);
    const deliverableRows = byDeliverable.map((g)=>({
            id: g.deliverable,
            deliverable: g.deliverable,
            span: g.first ? g.first === g.last ? cmDate(g.first, '—') : `${cmDate(g.first, '—')} – ${cmDate(g.last, '—')}` : '—',
            count: g.rows.length,
            results: countsText(resultCounts(g.rows.map((i)=>rows[i])), started)
        }));
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.assistant.deliveryActionBulkSummary,
        "data-count": rows.length,
        children: [
            groups.map((g)=>/*#__PURE__*/ _jsxs("div", {
                    className: "border-b border-border px-3 py-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            size: "body-sm",
                            className: "block font-medium",
                            children: g.label || g.action
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            size: "meta",
                            tone: "muted",
                            className: "block",
                            children: bulkGroupLine(g)
                        })
                    ]
                }, g.group)),
            progress ? /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-[var(--space-0-5)] border-b border-border px-3 py-2",
                "data-testid": Testids.assistant.deliveryActionBulkProgress,
                children: [
                    /*#__PURE__*/ _jsx(SpecraProgress, {
                        "aria-label": "Applying the changes",
                        value: progress.total ? progress.done / progress.total * 100 : 0,
                        size: "thin"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: `Applying ${progress.done} of ${progress.total}… Keep this tab open until it finishes.`
                    })
                ]
            }) : null,
            /*#__PURE__*/ _jsx("div", {
                className: "max-h-64 overflow-auto",
                "data-testid": Testids.assistant.deliveryActionBulkDeliverables,
                children: /*#__PURE__*/ _jsx(SpecraDataTable, {
                    columns: [
                        {
                            id: 'deliverable',
                            header: 'Deliverable',
                            cell: (r)=>r.deliverable
                        },
                        {
                            id: 'span',
                            header: 'Dates due',
                            cell: (r)=>r.span
                        },
                        {
                            id: 'count',
                            header: 'Changes',
                            cell: (r)=>r.count
                        },
                        // Nothing has a result before Accept, so the column waits for it.
                        ...started ? [
                            {
                                id: 'results',
                                header: 'Result',
                                cell: (r)=>r.results
                            }
                        ] : []
                    ],
                    rows: deliverableRows,
                    getRowId: (r)=>r.id,
                    framed: false
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-[var(--space-1)] border-t border-border px-3 py-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        size: "sm",
                        variant: "ghost",
                        "data-testid": Testids.assistant.deliveryActionBulkShowRows,
                        onClick: ()=>setShowRows((v)=>!v),
                        children: showRows ? 'Hide the rows' : `Show all ${rows.length} rows`
                    }),
                    refusedTotal > 0 ? /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        size: "sm",
                        variant: "ghost",
                        onClick: ()=>setShowRefused((v)=>!v),
                        children: showRefused ? 'Hide rows not proposed' : `${refusedTotal} not proposed: why`
                    }) : null,
                    unchangedTotal > 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: `${unchangedTotal} already as asked, left out`
                    }) : null
                ]
            }),
            showRefused && refusedTotal > 0 ? /*#__PURE__*/ _jsxs("div", {
                className: "flex max-h-48 flex-col gap-[var(--space-0-5)] overflow-auto border-t border-border px-3 py-2",
                "data-testid": Testids.assistant.deliveryActionBulkRefused,
                children: [
                    groups.map((g)=>Object.entries(g.refusedReasons ?? {}).map(([why, n])=>/*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                className: "font-medium",
                                children: `${n} × ${why}`
                            }, `${g.group}-${why}`))),
                    refused.map((r)=>/*#__PURE__*/ _jsx(SpecraText, {
                            size: "meta",
                            tone: "muted",
                            children: refusalLine(r)
                        }, `${r.row}-${r.ref ?? ''}`)),
                    refused.length < refusedTotal ? /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: `…and ${refusedTotal - refused.length} more.`
                    }) : null
                ]
            }) : null,
            showRows ? /*#__PURE__*/ _jsxs("div", {
                className: "border-t border-border",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "max-h-80 overflow-auto",
                        children: /*#__PURE__*/ _jsx(SpecraDataTable, {
                            columns: columns,
                            rows: slice,
                            getRowId: (r)=>r.id,
                            framed: false
                        })
                    }),
                    pages > 1 ? /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between px-3 py-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                children: `${safePage * BULK_PAGE_SIZE + 1}–${Math.min(rows.length, (safePage + 1) * BULK_PAGE_SIZE)} of ${rows.length}`
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "flex gap-[var(--space-1)]",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        type: "button",
                                        size: "sm",
                                        variant: "outline",
                                        disabled: safePage <= 0,
                                        onClick: ()=>setPage(safePage - 1),
                                        children: "Previous"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        type: "button",
                                        size: "sm",
                                        variant: "outline",
                                        disabled: safePage >= pages - 1,
                                        onClick: ()=>setPage(safePage + 1),
                                        children: "Next"
                                    })
                                ]
                            })
                        ]
                    }) : null
                ]
            }) : null
        ]
    });
}
